import { components } from "@certn/api/generated-schemas/client-portal";
import { JsonFormsCore } from "@jsonforms/core";
import { createContext, SetStateAction, useContext } from "react";

export interface SelectedCheckAddOn {
  key: string;
  name: string;
  price: number | null;
  currency: string | null;
}

export interface SelectedChecksType {
  key: string; // Check type
  name: string;
  cost: number;
  currency: string;
  check_arguments: Record<string, unknown>;
  required_check_types?: string[]; // Stores the required check types for the check and its children
  must_be_ordered_with_one_of?: string[];
  children?: components["schemas"]["CheckListChild"][];
  selected_options?: components["schemas"]["UmbrellaCheckSelectOption"][];
  // Some other components use selectedOptions, while others expect selected_options, so I added it here to fix OrderSummary without breaking existing functionality.
  selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][];
  parent_type?: string;
  ordering_type?: string;
  price_is_catalog_minimum?: boolean;
  add_on_options?: SelectedCheckAddOn[];
}

export interface SelectedBundlesType {
  id: string;
  title: string;
  products: components["schemas"]["AvailableBundleProductsListChild"][];
  currency: components["schemas"]["CurrencyEnum"];
  total: number;
  price?: string;
  check_types_with_arguments: Record<string, unknown>;
  checks?: {
    type: string;
    name: string;
    /** Format: double */
    price: number;
    currency: components["schemas"]["CurrencyEnum"];
    estimated_turnaround_time: string;
    disclaimers_icon_list: string[];
    disclaimers: string[];
    description: string;
    info_paragraphs: string[][];
    key_features: string[][];
    coverage: string;
    how_it_can_help_you: string[][];
    select_field_descriptions: string[][];
    select_field_endpoint: string;
    check_has_ordering_error_for_customer_group: string | null;
    mutually_exclusive_check_types: string[];
    umbrella_child_checks_heading: string | null;
    required_check_types: string[];
    select_options: components["schemas"]["BundledDealUmbrellaCheckSelectOption"][];
    children: components["schemas"]["BundledDealCheckListChild"][];
    check_arguments: string | Record<string, unknown>;
  }[];
}

export interface Package {
  id: string;
  name: string;
  price: {
    price: number;
    currency: string;
  };
  checks: {
    id: string;
    name: string;
    selected_options: components["schemas"]["CheckDetails"]["selected_options"];
    check_arguments?: components["schemas"]["CheckDetails"]["check_arguments"];
    add_on_options?: SelectedCheckAddOn[];
  }[];
  bundles: string[];
}

export type NotificationChannel =
  components["schemas"]["NotificationChannelsEnum"];

export type RecipientMobileNumberStatus = "empty" | "valid" | "invalid";

export interface RecipientMobileNumber {
  number: string | null;
  status: RecipientMobileNumberStatus;
}

export interface Order {
  checks: SelectedChecksType[];
  recipients: string[];
  tags: { id: string; tagValue: string }[];
  message?: string;
  group: string;
  bundles: SelectedBundlesType[];
  packageData?: Package;
  rescreenFrequency?: string;
  clientFormDataAndErrors: {
    data: JsonFormsCore["data"];
    errors: string[];
  };
  applicant_language?: string | null;
  cv_review_id?: string | null;
  notification_channels?: NotificationChannel[];
  recipient_mobile_numbers?: Record<string, RecipientMobileNumber>;
}

export const newOrder: Order = {
  checks: [],
  recipients: [],
  message: "",
  group: "",
  tags: [],
  bundles: [],
  clientFormDataAndErrors: {
    data: {},
    errors: [],
  },
  applicant_language: null,
  cv_review_id: null,
  notification_channels: [],
  recipient_mobile_numbers: {},
};

export interface OrderContextType {
  order: Order;
  currentStep: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setOrder: (order: SetStateAction<Order>) => void;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  blockOrder: boolean;
  /**
   * @deprecated Use setCheckValidationError instead for per-check validation tracking
   */
  setBlockOrder: (blockOrder: boolean) => void;
  /**
   * Set or clear a validation error for a specific check type.
   * When hasError is true, the check is added to the validation errors set.
   * When hasError is false, the check is removed from the set.
   * The order is blocked if any check has a validation error.
   */
  setCheckValidationError: (checkType: string, hasError: boolean) => void;
  /**
   * Clear all validation errors for a check type when it's deselected.
   */
  clearCheckValidationError: (checkType: string) => void;
  isQuickscreen: boolean;
}

export const OrderContext = createContext<OrderContextType>({
  order: {
    checks: [],
    recipients: [],
    message: "",
    group: "",
    tags: [],
    bundles: [],
    clientFormDataAndErrors: {
      data: {},
      errors: [],
    },
    applicant_language: null,
    cv_review_id: null,
    notification_channels: [],
    recipient_mobile_numbers: {},
  },
  isLoading: false,
  setIsLoading: (isLoading: boolean) => {},
  setOrder: () => {},
  currentStep: 0,
  goToNextStep: () => {},
  goToPrevStep: () => {},
  blockOrder: false,
  setBlockOrder: (blockOrder: boolean) => {},
  setCheckValidationError: (checkType: string, hasError: boolean) => {},
  clearCheckValidationError: (checkType: string) => {},
  isQuickscreen: false,
});

export const useOrderContext = () => {
  return useContext(OrderContext);
};
